import { SocketClient } from "cbcore-ts"
import { CALL, IF, IS_NOT_LIKE_NULL, NO, RETURNER, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CBColor } from "./Custom components/CBColor"
import { CBDataViewCellDescriptor } from "./Custom components/CBDataView"
import { CellView } from "./Custom components/CellView"
import { RHColor } from "./Custom components/RH/RHColor"
import { RHDataView } from "./Custom components/RH/RHDataView"
import { RHLabeledSearchableDropdown } from "./Custom components/RH/RHLabeledSearchableDropdown"
import { LanguageService } from "./LanguageService"


const numbersArrayWithRange = (start: number, end: number) => Array.from({ length: (end - start) }, (v, k) => k + start)

export const numberFormatter = new Intl.NumberFormat(
    "et-EE",
    {
        maximumFractionDigits: 1
    }
)
export const currencyFormatter = new Intl.NumberFormat(
    "et-EE",
    {
        style: "currency",
        currency: "EUR"
    }
)
export const percentFormatter = new Intl.NumberFormat(
    "et-EE",
    {
        style: "percent",
        maximumFractionDigits: 1
    }
)


export class RHRStatisticsViewController extends UIViewController {
    
    entityType?: "procurer" | "offerer" // Default, will be set by route
    
    get titleKey(): string {
        
        const result = "" + this.class.name + "Title_" + (this.entityType ?? "procurer")
        return result
        
    }
    
    explanationLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        // Text will be set dynamically based on entityType
    })
    
    comparisonEntityDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            // Label text will be set dynamically in handleRoute
            dropdown: {
                data: [], // Data will be loaded dynamically based on entityType in handleRoute
                dialogContainerView: this.view,
                isSingleSelection: YES,
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                            const route = UIRoute.currentRoute
                            const selectedCode = this.comparisonEntityDropdown.selectedItemCodes.firstElement ?? ""
                            
                            // Add the correct parameter based on entityType
                            const parameterName = this.entityType === "procurer"
                                ? RHRStatisticsViewController.ParameterIdentifierName.procurer
                                : RHRStatisticsViewController.ParameterIdentifierName.offerer
                            const updatedRoute = route.routeBySettingParameterInComponent(
                                RHRStatisticsViewController.routeComponentName,
                                parameterName,
                                selectedCode
                            )
                            
                            // Determine if we should replace history or push new entry
                            const existingParameter = route.componentWithViewController(this.class)?.parameters[parameterName]
                            
                            if (existingParameter) {
                                updatedRoute.apply()
                            }
                            else {
                                updatedRoute.applyByReplacingCurrentRouteInHistory()
                            }
                            
                        }
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)
    
    periodDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            label: { text: "Vali perioodid" },
            dropdown: {
                data: numbersArrayWithRange(2023, new Date().getFullYear() + 1).map(name => {
                        return {
                            _id: name + "",
                            title: LanguageService.localizedTextObjectForText(name + ""),
                            isADropdownDataSection: NO,
                            isADropdownDataRow: YES,
                            attachedObject: name + "",
                            itemCode: name + "",
                            dropdownCode: "Periods"
                        }
                    }
                ),
                // selectedItemCodes: UIRoute.currentRoute.componentWithName(
                //         "rhrstatistics"
                //     )?.parameters?.periods?.split("+") ||
                //     numbersArrayWithRange(
                //         new Date().getFullYear() - 1,
                //         new Date().getFullYear() + 1
                //     ).map(number => number + ""),
                dialogContainerView: this.view,
                isSingleSelection: NO,
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                            const route = UIRoute.currentRoute
                                .routeBySettingParameterInComponent(
                                    RHRStatisticsViewController.routeComponentName,
                                    RHRStatisticsViewController.ParameterIdentifierName.periods,
                                    this.periodDropdown.selectedItemCodes.join("+") ?? ""
                                )
                            
                            if (UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.periods]) {
                                route.apply()
                            }
                            else {
                                route.applyByReplacingCurrentRouteInHistory()
                            }
                        }
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)
    
    procurementProcedureCodeDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            label: { text: "Menetlusliik" },
            dropdown: {
                data: [], // Data loaded dynamically
                dialogContainerView: this.view,
                isSingleSelection: NO, // Allow multiple selections
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                            const route = UIRoute.currentRoute
                                .routeBySettingParameterInComponent(
                                    RHRStatisticsViewController.routeComponentName,
                                    RHRStatisticsViewController.ParameterIdentifierName.procType, // Use new param name
                                    this.procurementProcedureCodeDropdown.selectedItemCodes.join("+") ?? "" // Join selected codes
                                )
                            
                            // Decide whether to replace or push history based on if the param already exists
                            if (UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]) {
                                route.apply()
                            }
                            else {
                                route.applyByReplacingCurrentRouteInHistory()
                            }
                        }
                        // Trigger data update when selection changes
                        this.updateStatisticsView()
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)
    
    statisticsView = new RHDataView<{ statName: string } & Record<string, any>>()
        .configuredWithObject({
            // isSearchAvailable: NO,
            isTreeView: YES
        }).addedAsSubviewToView(this.view)
    bottomView = new UIView().addedAsSubviewToView(this.view)
    
    constructor(view: UIView) {
        
        super(view)
        
        // Code for further setup if necessary
        
        this.view.configureWithObject({
            backgroundColor: RHColor.backgroundColor
        })
        
    }
    
    
    private updateUITextsBasedOnEntityType() {
        if (this.entityType === "procurer") {
            this.explanationLabel.text = "Siin on võimalik näha Riigihanke registri avaliku info põhjal koostatud statistikat valitud hankija kohta."
        }
        else {
            this.explanationLabel.text = "Siin on võimalik näha Riigihanke registri avaliku info põhjal koostatud statistikat valitud pakkuja kohta."
        }
        this.view.setNeedsLayout() // Ensure layout updates if text changes size
    }
    
    private async retrieveComparisonEntityNamesAndInitDropdown() {
        
        let entityNames: string[] = []
        let dropdownCode: string
        if (this.entityType === "procurer") {
            entityNames = (await SocketClient.RetrieveProcurerNames()).result
            this.comparisonEntityDropdown.label.text = "Vali hankija"
            dropdownCode = "ProcurerNames"
        }
        else if (this.entityType === "offerer") {
            entityNames = (await SocketClient.RetrieveOffererNames()).result
            this.comparisonEntityDropdown.label.text = "Vali pakkuja"
            dropdownCode = "OffererNames"
        }
        this.comparisonEntityDropdown.dropdown.data = entityNames.map(name => ({
            _id: name,
            title: LanguageService.localizedTextObjectForText(name),
            isADropdownDataSection: NO,
            isADropdownDataRow: YES,
            attachedObject: name,
            itemCode: name,
            dropdownCode: dropdownCode // Use explicitly set dropdownCode
        }))
        
        this.comparisonEntityDropdown.enabled = YES
        
    }
    
    private async retrieveProcurementProcedureCodesAndInitDropdown() {
        
        try {
            
            const procedureCodes = (await SocketClient.RetrieveProcurementProcedureCodes()).result
            const data = Object.entries(procedureCodes).map(([code, name]) => ({
                _id: code,
                title: LanguageService.localizedTextObjectForKey(
                    code + "_procurement_type",
                    name
                ),
                isADropdownDataSection: NO,
                isADropdownDataRow: YES,
                attachedObject: code,
                itemCode: code,
                dropdownCode: "ProcurementProcedureCodes"
            }))
            this.procurementProcedureCodeDropdown.dropdown.data = data
            
            this.procurementProcedureCodeDropdown.selectedItemCodes = procedureCodes.allKeys
            
            // const procurementProcedureCodes = UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]
            // UIRoute.currentRoute.routeBySettingParameterInComponent(
            //     RHRStatisticsViewController.routeComponentName,
            //     RHRStatisticsViewController.ParameterIdentifierName.procType,
            //     procurementProcedureCodes || procedureCodes.join("+")
            // ).applyByReplacingCurrentRouteInHistory()
            
            this.procurementProcedureCodeDropdown.enabled = YES
            
        } catch (error) {
            console.error("Error fetching procurement types:", error)
        }
        
    }
    
    async updateStatisticsView() {
        
        const routeComponent = this.routeComponent
        const entityName = routeComponent
            ?.parameters[routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.entityType]]
        if (!entityName || !this.periodDropdown.dropdown.selectedItemCodes.length || !this.procurementProcedureCodeDropdown.selectedItemCodes.length) {
            this.statisticsView.data = []
            this.statisticsView.descriptors = []
            return
        }
        
        const periods = this.periodDropdown.selectedItemCodes
            .map(period => period.integerValue)
            .sort()
            .map(period => period + "")
        const descriptors: CBDataViewCellDescriptor<{ statName: string } & Record<string, any>>[] = [
            {
                keyPath: "statName",
                title: "",
                allowSorting: NO,
                updateCell: (
                    cellView: CellView,
                    dataItem: any
                ) => {
                    
                    cellView.titleLabel.text = LanguageService.stringForKey(
                        dataItem.statName,
                        LanguageService.currentLanguageKey,
                        dataItem.statName
                    )
                    
                }
            }
        ]
        
        periods.forEach(period => {
            const periodPrefix = period + "_"
            const descriptor: CBDataViewCellDescriptor<{ statName: string } & Record<string, any>> = {
                keyPath: periodPrefix + "statValue",
                title: period,
                allowSorting: NO,
                updateCell: (cellView, dataItem) => {
                    
                    cellView.backgroundColor = IF(dataItem.isSubDataItem)(
                        RETURNER(CBColor.lightGreyColor)
                    ).ELSE(
                        RETURNER(CBColor.whiteColor)
                    )
                    
                }
            }
            descriptors.push(descriptor)
        })
        
        this.statisticsView.descriptors = descriptors
        this.statisticsView.hasResizableColumns = YES
        this.statisticsView.updateRowWithNestingDepth = (nestingDepth, rowView) => {
            
            // If there is depth, add another subview to the background view
            
            if (nestingDepth && !rowView.backgroundView.subviews.length) {
                
                rowView.backgroundView.addSubview(new UIView(
                    rowView.elementID + "_BackgroundViewNestingDepthIndicatorShaderView_added_from_" + this.class.name
                ).configuredWithObject({
                    backgroundColor: CBColor.blackColor,
                    setPosition: CALL(0, 0, 0, 0)
                }))
                
            }
            else if (nestingDepth && rowView.backgroundView.subviews.length) {
                
                rowView.backgroundView.subviews.firstElement.alpha = nestingDepth * 0.08
                
            }
            else if (!nestingDepth) {
                
                rowView.backgroundView.subviews.everyElement.removeFromSuperview()
                
            }
            
        }
        
        let statisticsViewData: ({ statName: string } & Record<string, any>)[] = []
        const procurementProcedureCodes = this.procurementProcedureCodeDropdown.selectedItemCodes
        // Check if at least one procurement type is selected
        if (procurementProcedureCodes.length === 0) {
            this.statisticsView.data = [] // Clear data if no type selected
            return
        }
        
        let statisticsObject: any
        if (this.entityType === "procurer") {
            statisticsObject = (await SocketClient.ProcurerStatisticsForPeriods({
                procurerName: entityName,
                periods: periods,
                procurementProcedureCodes: procurementProcedureCodes,
                selfTest: routeComponent?.parameters.selfTest == "YES"
            })).result
        }
        else { // entityType === "offerer"
            statisticsObject = (await SocketClient.OffererStatisticsForPeriods({
                offererName: entityName,
                periods: periods,
                procurementProcedureCodes: procurementProcedureCodes,
                selfTest: routeComponent?.parameters.selfTest == "YES"
            })).result
        }
        
        // Keys are expected to be in the format {year}_{statName}
        // Need to extract the unique stat names across the selected periods.
        // We need to extract the unique stat names across the selected periods.
        
        const allKeys = statisticsObject.allKeys as string[]
        
        // Figure out the unique stat names (part after year_)
        // Filter keys based on selected periods
        const rowKeys = [
            ...new Set(
                allKeys
                    .filter(key => {
                        const mainKey = (key + "").split("_").lastElement
                        if (mainKey.startsWith("percentageOf")) {
                            if (allKeys.contains(key.replace("percentageOf", "numberOf"))) {
                                return NO
                            }
                        }
                        return YES
                    })
                    .map((key: string) => {
                        // Extract the stat name part (e.g., contractMonetarySum)
                        const parts = key.split("_")
                        return parts.lastElement
                    }).filter((key: undefined | string) => key) // Filter out undefined/empty keys
            )
        ]
        
        // Add values into the rows
        statisticsViewData = rowKeys.map((key: string) => {
            const result: { statName: string } & Record<string, any> = {
                statName: LanguageService.stringForKey(key, LanguageService.currentLanguageKey, key)
            }
            periods.forEach(period => {
                const fullKey = `${period}_${key}`
                const value = statisticsObject[fullKey]
                let displayValue: string | number = "N/A" // Default to N/A
                function isAnArrayOfStrings(value: any): value is string[] {
                    return Array.isArray(value) && value.every(item => typeof item === "string")
                }
                
                if (key == "offerersDataWithCount") {
                    
                    var asdasdasdasdasjhfdksdikuyhfgf = 1 //value.map(dataObject => dataObject)
                    
                    
                }
                
                if (key == "offerersDataWithMonetarySum") {
                    
                    var asdasdasdasdasd = 1
                    
                }
                
                if (value !== undefined && value !== null) {
                    
                    if (key.startsWith("numberOf")) {
                        const numberValue = Number(value)
                        const percentageKey = key.replace("numberOf", "percentageOf")
                        const fullPercentageKey = `${period}_${percentageKey}`
                        const percentageValue = statisticsObject[fullPercentageKey]
                        
                        if (IS_NOT_LIKE_NULL(percentageValue)) {
                            // Both number and percentage exist
                            displayValue = `${numberFormatter.format(numberValue)} (${percentFormatter.format(Number(
                                percentageValue * 0.01))})`
                        }
                        else {
                            // Only number exists
                            displayValue = numberFormatter.format(numberValue)
                        }
                    }
                    else if (key.startsWith("percentageOf")) {
                        // This handles cases where the 'numberOf' row was filtered out,
                        // but we still want to show the percentage row.
                        displayValue = `${Number(value)}%`
                    }
                    else if (key === "contractMonetarySum" && typeof value === "number") {
                        displayValue = currencyFormatter.format(value)
                    }
                    else if (typeof value === "number") {
                        displayValue = numberFormatter.format(value)
                    }
                    else if (isAnArrayOfStrings(value)) {
                        displayValue = value.join(", ")
                    }
                    else {
                        // Handle other non-numeric string values if necessary
                        displayValue = value
                    }
                }
                // Use a consistent key structure for the data view row item
                result[`${period}_statValue`] = displayValue
                
                if (isAnArrayOfStrings(value)) {
                    
                    result.subData = result.subData || []
                    
                    const stringsToShowAsSubdataItems = value
                    
                    stringsToShowAsSubdataItems.forEach((string, index) => {
                        
                        // Find the corresponding subdata item or add a new one and set the string
                        
                        const subdataItem = result.subData[index] || { isSubDataItem: YES }
                        
                        subdataItem[`${period}_statValue`] = "&nbsp;".repeat(5) + string.italics()
                        
                        result.subData[index] = subdataItem
                        
                    })
                }
                
            })
            return result
        })
        
        this.statisticsView.data = statisticsViewData
        this.statisticsView.setTreeData(statisticsViewData, "subData", NO)
        
        
        this.view.setNeedsLayoutUpToRootView()
        
    }
    
    
    static override readonly routeComponentName = "rhrstatistics"
    
    static override readonly ParameterIdentifierName = {
        "entityType": "entityType",
        "procurer": "procurer",
        "offerer": "offerer",
        "periods": "periods",
        "procType": "procType",
        "selfTest": "selfTest"
    }
    
    override async viewWillAppear() {
        
        this.retrieveProcurementProcedureCodesAndInitDropdown()
        this.retrieveComparisonEntityNamesAndInitDropdown()
        
        this.periodDropdown.selectedItemCodes = numbersArrayWithRange(
            new Date().getFullYear() - 2,
            new Date().getFullYear() + 1
        ).map(name => name + "")
        this.periodDropdown.enabled = YES
        
    }
    
    override async viewDidAppear() {
        
        const routeComponent = this.routeComponent
        const entityName = routeComponent
            ?.parameters[routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.entityType]]
        
        if (!entityName) {
            
            this.comparisonEntityDropdown.dropdown.openDropdown()
            this.comparisonEntityDropdown.dropdown._searchTextField.focus()
            this.comparisonEntityDropdown.dropdown._searchTextField.text = ""
            
        }
        
        var asd = 1
        
    }
    
    
    override async viewWillDisappear() {
        
    }
    
    
    override async handleRoute(route: UIRoute) {
        
        await super.handleRoute(route)
        
        // Determine entityType from the route parameter, default to 'procurer' if missing
        const entityTypeFromRoute = this.routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.entityType] as "procurer" | "offerer"
        
        // Set the (default) entity type in the route if it is missing
        if (!entityTypeFromRoute && this.routeComponent) {
            
            route.routeBySettingParameterInComponent(
                RHRStatisticsViewController.routeComponentName,
                RHRStatisticsViewController.ParameterIdentifierName.entityType,
                "procurer"
            ).applyByReplacingCurrentRouteInHistory()
            
            return
            
        }
        
        // Only update dropdown data if entityType changed
        if (entityTypeFromRoute !== this.entityType) {
            this.entityType = entityTypeFromRoute
            this.updateUITextsBasedOnEntityType()
            
            console.log("Entity type changed, fetching new names for:", this.entityType) // Add log for debugging
            await this.retrieveComparisonEntityNamesAndInitDropdown()
            
        }
        
        // Set selected entity from route (always do this, as the selected entity might change without type changing)
        const selectedEntity = decodeURIComponent(this.routeComponent?.parameters[this.entityType] ?? "")
        this.comparisonEntityDropdown.dropdown.selectedItemCodes = [selectedEntity].filter(p => p)
        
        // Set selected periods
        this.periodDropdown.dropdown.selectedItemCodes = decodeURIComponent(
            this.routeComponent?.parameters.periods ?? ""
        ).split("+").filter(p => p) // Filter out empty strings if periods parameter is empty
        
        // Set selected procurement types (split by '+') only if data is loaded
        const selectedProcurementProcedureCodes: string[] = decodeURIComponent(
            this.routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType] ?? this.periodDropdown.dropdown.data
                .map(object => object.itemCode).slice(-2).join("+")
        ).split("+").filter(p => p) // Filter out empty strings
        if (!this.procurementProcedureCodeDropdown.dropdown.data.length) {
            await this.retrieveProcurementProcedureCodesAndInitDropdown()
        }
        this.procurementProcedureCodeDropdown.dropdown.selectedItemCodes = selectedProcurementProcedureCodes
        
        await this.updateStatisticsView()
        
        
    }
    
    
    override updateViewConstraints() {
        
        super.updateViewConstraints()
        
    }
    
    
    override updateViewStyles() {
        
        super.updateViewStyles()
        
    }
    
    
    override viewDidLayoutSubviews() {
        
        super.viewDidLayoutSubviews()
        
    }
    
    
    override layoutViewSubviews() {
        
        super.layoutViewSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        
        // View bounds
        const bounds = this.view.bounds.rectangleWithInset(padding)
        
        // Position explanationLabel at the top
        this.explanationLabel.frame = bounds.rectangleWithHeight(
            [this.explanationLabel.intrinsicContentHeight(bounds.width), labelHeight].max()
        )
        
        // Calculate width for three dropdowns
        const dropdownWidth = (bounds.width - padding * 2) / 3
        const dropdownHeight = [
            this.comparisonEntityDropdown.intrinsicContentHeight(dropdownWidth),
            this.periodDropdown.intrinsicContentHeight(dropdownWidth),
            this.procurementProcedureCodeDropdown.intrinsicContentHeight(dropdownWidth),
            padding
        ].max()
        
        this.comparisonEntityDropdown.frame = this.explanationLabel.frame.rectangleForNextRow(
            padding,
            dropdownHeight
        ).rectangleWithWidth(dropdownWidth)
        
        this.periodDropdown.frame = this.comparisonEntityDropdown.frame.rectangleForNextColumn(
            padding
        )
        
        this.procurementProcedureCodeDropdown.frame = this.periodDropdown.frame.rectangleForNextColumn(
            padding
        )
        
        this.statisticsView.frame = this.comparisonEntityDropdown.frame.rectangleWithWidth(bounds.width)
            .rectangleForNextRow(
                padding,
                [this.statisticsView.intrinsicContentHeight(bounds.width), padding].max()
            )
        this.bottomView.frame = this.statisticsView.frame.rectangleForNextRow(padding, 10)
        
        
    }
}


